import apiClient from './ApiClient';
import SessionManager from '../utils/SessionManager';
import { API_CONFIG } from '../config/api';

/**
 * Employee Authentication Service - Manages employee authentication and session state
 * Handles employee login, logout, token management, and session validation
 */
class EmployeeAuthService {
  constructor() {
    this.isLoggingIn = false;
    this.currentEmployee = null;
    this.tokenStorageKey = 'seemanchal_employee_token';
    this.userStorageKey = 'seemanchal_employee_user';
  }

  /**
   * Employee login with username and password
   * API expects: {"username": "string", "password": "string"}
   * API returns: {"access_token": "string", "token_type": "string"}
   */
  async login(credentials) {
    if (this.isLoggingIn) {
      throw new Error('Login already in progress');
    }

    if (!credentials || !credentials.username || !credentials.password) {
      throw new Error('Username and password are required');
    }

    this.isLoggingIn = true;

    try {
      // Make login request to API
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.EMPLOYEE_LOGIN, {
        username: credentials.username.trim(),
        password: credentials.password
      }, { skipAuth: true }); // Skip auth for login request

      // Validate response format
      if (!response.access_token) {
        throw new Error('Invalid response format: missing access_token');
      }

      // Store token and employee info
      const success = SessionManager.setToken(response.access_token, this.tokenStorageKey);
      if (!success) {
        throw new Error('Failed to store authentication token');
      }

      // Create employee info from response
      const employeeInfo = {
        username: credentials.username,
        token_type: response.token_type || 'bearer',
        loginTime: new Date().toISOString()
      };

      SessionManager.setUser(employeeInfo, this.userStorageKey);
      this.currentEmployee = employeeInfo;

      return {
        success: true,
        employee: employeeInfo,
        token: response.access_token,
        message: 'Login successful'
      };

    } catch (error) {
      // Handle specific login errors
      if (error.status === 401) {
        throw new Error('Invalid username or password');
      } else if (error.status === 422) {
        throw new Error('Invalid credentials format');
      } else if (error.status === 429) {
        throw new Error('Too many login attempts. Please try again later.');
      } else if (error.name === 'NetworkError') {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      } else if (error.name === 'TimeoutError') {
        throw new Error('Login request timed out. Please try again.');
      }
      
      // Re-throw with original message or generic error
      throw new Error(error.message || 'Login failed. Please try again.');
    } finally {
      this.isLoggingIn = false;
    }
  }

  /**
   * Logout and clear all session data
   */
  logout() {
    try {
      // Clear all session data
      SessionManager.clearSession(this.tokenStorageKey, this.userStorageKey);
      this.currentEmployee = null;
      
      return {
        success: true,
        message: 'Logged out successfully'
      };
    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, try to clear what we can
      SessionManager.clearSession(this.tokenStorageKey, this.userStorageKey);
      this.currentEmployee = null;
      
      return {
        success: false,
        message: 'Logout completed with errors'
      };
    }
  }

  /**
   * Check if employee is currently authenticated
   */
  isAuthenticated() {
    return SessionManager.isAuthenticated(this.tokenStorageKey);
  }

  /**
   * Get current authentication token
   */
  getToken() {
    return SessionManager.getToken(this.tokenStorageKey);
  }

  /**
   * Get authentication token for API requests
   */
  getAuthToken() {
    return SessionManager.getToken(this.tokenStorageKey);
  }

  /**
   * Get current employee information
   */
  getCurrentEmployee() {
    if (this.currentEmployee) {
      return this.currentEmployee;
    }
    
    // Try to load from storage
    this.currentEmployee = SessionManager.getUser(this.userStorageKey);
    return this.currentEmployee;
  }

  /**
   * Validate current session with server
   */
  async validateSession() {
    if (!this.isAuthenticated()) {
      return false;
    }

    try {
      const token = this.getToken();
      if (!token) {
        this.logout();
        return false;
      }

      // Check if token is expiring soon
      if (SessionManager.isTokenExpiringSoon(5, this.tokenStorageKey)) {
        console.warn('Token expiring soon');
      }

      return true;
    } catch (error) {
      console.error('Session validation error:', error);
      this.logout();
      return false;
    }
  }

  /**
   * Initialize authentication state on app startup
   */
  async initialize() {
    try {
      // Load employee from storage
      this.currentEmployee = SessionManager.getUser(this.userStorageKey);
      
      // Validate session if employee exists
      if (this.currentEmployee) {
        const isValid = await this.validateSession();
        if (!isValid) {
          this.currentEmployee = null;
        }
      }
      
      return this.isAuthenticated();
    } catch (error) {
      console.error('Employee auth initialization error:', error);
      this.logout();
      return false;
    }
  }

  /**
   * Check if login is currently in progress
   */
  isLoginInProgress() {
    return this.isLoggingIn;
  }
}

// Export singleton instance
const employeeAuthService = new EmployeeAuthService();
export default employeeAuthService;

